import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, PenLine, Code2, HeartHandshake } from "lucide-react";
import Portfolio from "@/components/Portfolio";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PageBackdrop } from "@/components/PageBackdrop";

const steps = [
  {
    icon: Compass,
    num: "01",
    title: "Discover",
    copy: "A short call about your business, your customers and what you need from the site.",
  },
  {
    icon: PenLine,
    num: "02",
    title: "Design",
    copy: "Structure and words come before colours. The site gets built around what your visitors actually need.",
  },
  {
    icon: Code2,
    num: "03",
    title: "Build",
    copy: "Clean, modern code you can review in stages. Fast, easy to update and made to last.",
  },
  {
    icon: HeartHandshake,
    num: "04",
    title: "Care",
    copy: "Launch day is the start. Updates, backups and small fixes handled by the person who built it.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
};

const WorkPage = () => {
  usePageMeta("Vagus Digital | Selected work & process");

  return (
    <>
      <div className="relative overflow-hidden pt-16 lg:pt-20">
        <PageBackdrop src="/art/work.webp" />
        <Portfolio />
      </div>
      <section className="border-t border-jade/10 bg-paper2/70 py-24 lg:py-32" data-testid="process-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.6 }}
            className="text-xs font-medium uppercase tracking-[0.3em] text-jade"
          >
            How I work
          </motion.p>
          <motion.h2
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.08 }}
            className="mt-4 font-display text-3xl font-light tracking-tight text-ink sm:text-4xl"
            data-testid="process-heading"
          >
            Four steps, <em className="italic text-jade">no surprises</em>.
          </motion.h2>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative mt-14 hidden md:block"
            aria-hidden="true"
          >
            <div className="absolute left-[12.5%] right-[12.5%] top-6 h-px bg-jade/20" />
            <div className="relative grid grid-cols-4">
              {steps.map((step) => (
                <div key={step.num} className="flex justify-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full border border-jade/25 bg-white font-display text-lg italic text-jade shadow-ambient">
                    {step.num}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>

          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 md:mt-6 lg:grid-cols-4">
            {steps.map((step, i) => (
              <motion.div
                key={step.num}
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.08 * i }}
                className="rounded-2xl bg-white p-8 shadow-ambient"
                data-testid={`process-step-${i}`}
              >
                <div className="flex items-center justify-between">
                  <step.icon className="h-6 w-6 text-jade" strokeWidth={1.75} />
                  <span className="font-display text-2xl font-light italic text-jade/40">
                    {step.num}
                  </span>
                </div>
                <h3 className="mt-6 font-display text-xl font-medium text-ink">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-ink2/80">
                  {step.copy}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.7 }}
            className="mt-16 flex justify-center"
          >
            <Link
              to="/contact"
              data-testid="work-cta-link"
              className="inline-flex items-center gap-2 rounded-full bg-jade px-8 py-3.5 text-sm font-medium text-paper transition-colors duration-300 hover:bg-ink"
            >
              Start a project <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default WorkPage;